import { put, takeLatest, retry } from 'redux-saga/effects';
import { PayloadAction } from 'redux-starter-kit';
import { saveAs } from 'file-saver';
import {
  Mutations,
  Requests,
  ResolverResponse,
  Resources,
} from '@erecruit/types';
import {
  getResourceApi,
  MatchSearchRequestWithDataPref,
  MatchSearchCountRequestWithDataPref,
} from '@erecruit/modules';

import {
  getFilterDataFail,
  getFilterDataSuccess,
  MatchSearchFilterData,
  searchMatchesFail,
  searchMatchesSuccess,
  exportMatchesSuccess,
  exportMatchesFail,
} from './reducer';
import { RETRY_DELAY_LENGTH, RETRY_MAX_TRIES } from '../../../constants';

function* searchMatches({
  payload,
}: PayloadAction<Resources.MatchSearchParams>) {
  try {
    const responses = yield getResourceApi().getResources([
      new MatchSearchRequestWithDataPref(payload),
      new MatchSearchCountRequestWithDataPref({
        filter: payload.filter,
      }),
    ]) as Promise<ResolverResponse[]>;

    const response = responses[0] as ResolverResponse;

    if (response.success) {
      const data = responses[0].data as Resources.MatchSearch[];
      const totalCount = responses[1].data[0].value as number;
      return yield put(searchMatchesSuccess({ data, totalCount }));
    }

    yield put(searchMatchesFail(response.message));
  } catch (e) {
    console.error(e);
    yield put(searchMatchesFail(e.message));
  }
}

function* getFilterData() {
  try {
    const pager = { offset: 0, limit: 100 };
    const responses = yield getResourceApi().getResources([
      new Requests.CompanyRequest({ pager }),
      new Requests.FacilityDepartmentRequest({ pager }),
      new Requests.ContactRequest({ pager }),
      new Requests.PositionRequest({ pager }),
      new Requests.PositionTypeRequest({ pager }),
      new Requests.MatchStatusRequest({ pager }),
      new Requests.WorksiteRequest({ pager }),
    ]) as Promise<ResolverResponse[]>;

    const response = responses[0] as ResolverResponse;

    if (response.success) {
      const filterData: MatchSearchFilterData = {
        companies: responses[0].data as Resources.Company[],
        facilityDepartments: responses[1]
          .data as Resources.FacilityDepartment[],
        positionOwners: responses[3].data as Resources.Contact[],
        positions: responses[3].data as Resources.Position[],
        positionTypes: responses[4].data as Resources.PositionType[],
        statuses: responses[5].data as Resources.MatchStatus[],
        worksites: responses[6].data as Resources.Worksite[],
      };
      return yield put(getFilterDataSuccess(filterData));
    }

    yield put(getFilterDataFail(response.message));
  } catch (e) {
    console.error(e);
    yield put(getFilterDataFail(e.message));
  }
}

function* exportMatches({
  payload,
}: PayloadAction<
  Pick<Mutations.ExportMatchSearchParams, 'columns'> & {
    params: Mutations.ExportMatchSearchParams['matchSearchParams'];
  }
>) {
  try {
    const { columns, params: matchSearchParams } = payload;
    const [{ fileId }] = yield getResourceApi().createMutationRequest(
      new Requests.ExportMatchSearchRequest({ columns, matchSearchParams }),
    );
    const blob = yield retry(RETRY_MAX_TRIES, RETRY_DELAY_LENGTH, () =>
      getResourceApi().getMedia(fileId, true),
    );

    saveAs(blob);

    yield put(exportMatchesSuccess({}));
  } catch (e) {
    yield put(exportMatchesFail(e.message));
  }
}

function* watchForRequestData() {
  yield takeLatest('matchSearch/searchMatches', searchMatches);
  yield takeLatest('matchSearch/getFilterData', getFilterData);
}

function* watchForExportMatches() {
  yield takeLatest('matchSearch/exportMatches', exportMatches);
}

export default [watchForRequestData(), watchForExportMatches()];
